import { centreElementInView } from 'lib/window/scrollUtils'
import React, { ComponentProps, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react'
import _StepperProgressStep from './_StepperProgressStep'
import _StepsTrack from './_StepsTrack'

export interface ProgressStep {
  key: string | number
  label: string
}

type StepProps = ComponentProps<typeof _StepperProgressStep>

function determineStepState(stepIndex: number, activeIndex: number): StepProps['state'] {
  if (stepIndex === activeIndex) return 'active'
  if (stepIndex < activeIndex) return 'completed'
  return 'inactive'
}

interface Props {
  /**
   * @remark _Please make sure this value is memoised._
   * @remark _The type can be exported as `ProgressStep`._
   */
  steps: Array<ProgressStep>
  /**
   * Must match one of the keys provided in the `steps` array.
   */
  activeStepKey: ProgressStep['key']
  /**
   * When provided, completed steps become interactive.
   */
  onStepClick?: ((stepKey: ProgressStep['key']) => void) | ((stepKey: ProgressStep['key'], event: MouseEvent<HTMLButtonElement>) => void)
}

/**
 * Given an array of steps and the currently active key,
 * it'll render the steps horizontally,
 * while maintaining their states according to the active key.
 *
 * The overflowing steps will be masked,
 * and the currently active step is always scrolled horizontally centre.
 */
function StepperProgressSteps(props: Props) {
  const { steps, activeStepKey, onStepClick } = props
  const statefulSteps = useMemo(() => {
    const currentActiveIndex = steps.findIndex((step) => step.key === activeStepKey)

    if (currentActiveIndex === undefined) { // active step is out of bounds!
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.trace(`Step "${activeStepKey}" is out of bounds!`, steps)
      return steps.map((step, index) => ({
        key: step.key,
        label: step.label,
        number: index + 1,
        state: 'inactive' as const,
      }))
    }

    return steps.map((step, index) => {
      return {
        key: step.key,
        label: step.label,
        number: index + 1,
        state: determineStepState(index, currentActiveIndex),
      }
    })
  }, [activeStepKey, steps])

  const handleStepClick = useCallback((stepKey: ProgressStep['key'], event: MouseEvent<HTMLButtonElement>) => {
    if (onStepClick) {
      onStepClick(stepKey, event)
    }
  }, [onStepClick])

  const stepsRefs = useRef<Record<ProgressStep['key'], HTMLButtonElement | null>>({})

  useEffect(() => {
    if (activeStepKey) {
      const activeStepElement = stepsRefs.current[activeStepKey]
      if (activeStepElement) {
        centreElementInView(activeStepElement)
      }
    }
  }, [activeStepKey])

  return <_StepsTrack>
    {statefulSteps.map((step, index) => <_StepperProgressStep
      {...step}
      key={step.key}
      stepKey={step.key}
      stepIndex={index}
      ref={el => stepsRefs.current[step.key] = el}
      onClick={onStepClick ? handleStepClick : undefined}
    />)}
  </_StepsTrack>
}

export default StepperProgressSteps
